/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable @typescript-eslint/indent */
import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormArray } from '@angular/forms';
import { reduceNumberSum } from 'src/app/util/common/math-util';
import { TotalControlNames } from '../pojo/fund-strategy-enums';
import { FundStrategyModel, TotalsFS } from '../pojo/fund-strategy-export-classes';
import { FsGroupFormGroup, SubGroupFormGroup } from '../pojo/fund-strategy-export-types';
import { DateService } from "../../../util/common/date-service/date.service";

@Injectable({
  providedIn: 'root',
})
export class FundStrategyService {

  constructor(private datePipe: DatePipe) {
  }

  /**
   * Calculation of subgroup totals => sum of all subgroup rounds
   */
  getSubGroupTotals(subGroup: SubGroupFormGroup, controlName: TotalControlNames): number {
    const roundsDataArray = subGroup.controls.roundsData;
    // Calculate total investment and total exit proceeds outside of conditional checks
    const totalInvestment = roundsDataArray.controls.reduce((sum, currentRound) => {
      const currentInvestment = Number(currentRound.controls.investment.value) || 0;
      const currentCompaniesNumber = Number(currentRound.controls.companiesNumber.value) || 0;
      return sum + (currentInvestment * currentCompaniesNumber);
    }, 0);

    const totalExitProceeds = roundsDataArray.controls.reduce((sum, currentRound) => {
      const holding = Number(currentRound.controls.holding.value) || 0;
      const exitCompNumber = Number(currentRound.controls.exitCompNumber.value) || 0;
      const exitCompPrice = Number(currentRound.controls.exitCompPrice.value) || 0;
      return sum + (holding * exitCompNumber * exitCompPrice);
    }, 0);

    const totalRound = roundsDataArray.controls.reduce((sum, currentRound) => {
      const round = Number(currentRound.controls.round.value) || 0;
      return sum + round;
    }, 0);

    switch (controlName) {
      case TotalControlNames.round:
        return totalRound;
      case TotalControlNames.investment:
        return totalInvestment;
      case TotalControlNames.exitProceeds:
        return totalExitProceeds;
      case TotalControlNames.gain:
        return totalExitProceeds - totalInvestment;
      case TotalControlNames.moic:
        return totalInvestment !== 0 ? totalExitProceeds / totalInvestment : 0; // Avoid division by zero
      default:
        const numberArray = roundsDataArray.controls.map((roundFormGroup) =>
          Number(roundFormGroup.controls[controlName].value) || 0,
        );
        return reduceNumberSum(numberArray);
    }
  }

  /**
   * Calculates total values for the group
   */
  calculateSubTotals(grIndex: number, fs: FormArray<FsGroupFormGroup>): TotalsFS {
    const subGroupsArray = fs.controls[grIndex].controls.subGroups;
    const totals = new TotalsFS();
    subGroupsArray.controls.forEach((subGroup) => {
      totals.companiesNumber += Number(subGroup.controls.roundsData.controls[0].controls.companiesNumber.value) || 0;
      totals.writeOff += this.getSubGroupTotals(subGroup as SubGroupFormGroup, TotalControlNames.writeOff);
      totals.exitCompNumber += this.getSubGroupTotals(subGroup as SubGroupFormGroup, TotalControlNames.exitCompNumber);
      totals.investment += this.getSubGroupTotals(subGroup as SubGroupFormGroup, TotalControlNames.investment);
      totals.proceed += this.getSubGroupTotals(subGroup as SubGroupFormGroup, TotalControlNames.exitProceeds);
    });

    totals.gain = totals.proceed - totals.investment;
    totals.moic = totals.proceed / totals.investment;

    return totals;
  }

  /**
   * Calculates total values for the model
   */
  calculateGrandTotal(subTotals?: TotalsFS[]): TotalsFS {
    const grandTotal = new TotalsFS();

    subTotals.forEach((subTotal) => {
      grandTotal.companiesNumber += subTotal.companiesNumber;
      grandTotal.writeOff += subTotal.writeOff;
      grandTotal.exitCompNumber += subTotal.exitCompNumber;
      grandTotal.investment += subTotal.investment;
      grandTotal.proceed += subTotal.proceed;
    });

    grandTotal.gain = grandTotal.proceed - grandTotal.investment;
    grandTotal.moic = grandTotal.proceed / grandTotal.investment;

    return grandTotal;
  }


  setLastSaveText(fsModels: FundStrategyModel[]): FundStrategyModel[] {
     fsModels.forEach((model) => {
      model.lastSaveText = this.formatAndSetLastSaveText(model);
    });
    return this.sortStrategiesByLastUpdateDate(fsModels);
  }

  formatAndSetLastSaveText(model: FundStrategyModel): string {
    const createdDateFormatted = DateService.createUTCDate(model.createdDate.toString());
    const lastUpdateDateFormatted = DateService.createUTCDate(model.lastUpdateDate.toString());
    const isCreated = createdDateFormatted === lastUpdateDateFormatted;

  return (isCreated ? 'Created' : 'Updated')
    + (model.createdBy ? (' by ' + model.createdBy) : '') + ': '
    + this.datePipe.transform(lastUpdateDateFormatted, 'yyyy-MM-dd')
    + ' - '
    + this.datePipe.transform(lastUpdateDateFormatted, 'HH:mm');
  }

  /**
   * Sorts an array of FundStrategyModels by the lastUpdateDate property.
   * @param models An array of FundStrategyModels to sort.
   * @return A new array sorted by lastUpdateDate in descending order.
   */
   sortStrategiesByLastUpdateDate(models: FundStrategyModel[]): FundStrategyModel[] {
    return models.sort((a, b) => {
      const dateA = new Date(a.lastUpdateDate).getTime();
      const dateB = new Date(b.lastUpdateDate).getTime();
      return dateB - dateA;
    });
  }


  mapToFundStrategyModel = (response: FundStrategyModel): FundStrategyModel => {
    const lastSaveText = this.formatAndSetLastSaveText(response);
    return new FundStrategyModel(response.uuid,
                                 response.name,
                                 response.totalCommitment,
                                 response.expectedExpenses,
                                 response.orgUUID,
                                 response.groups,
                                 lastSaveText,
                                 response.lastUpdateDate,
                                 response.createdDate,
                                 response.createdBy);
  };
}

